import { useState, useRef } from "react";
import { useApp } from "@/App";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, Send, Check, Phone, Mail, Hash, FileText, Banknote, MessageSquare, Download } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import jsPDF from "jspdf";

export default function HandoverPanel({ token, onClose }) {
  const { API, packages, addons } = useApp();
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    vehicle_brand: "",
    vehicle_spz: "",
    programs: "",
    price: 0,
    notes: "",
    location: "cestlice"
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSubmit = async () => {
    if (!form.customer_phone || !form.vehicle_brand || !form.vehicle_spz || !form.programs || !form.price) {
      toast.error("Vyplňte všechna povinná pole");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/handover?token=${token}`, form);
      if (form.customer_email) {
        toast.success("Vozidlo převzato! E-mail odeslán zákazníkovi.");
      } else {
        toast.success("Vozidlo převzato! Stáhněte PDF pro zákazníka.");
      }
      setDone(true);
    } catch (e) {
      toast.error("Chyba při odesílání");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({ customer_name: "", customer_phone: "", customer_email: "", vehicle_brand: "", vehicle_spz: "", programs: "", price: 0, notes: "", location: "cestlice" });
    setDone(false);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const w = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(5, 5, 5);
    doc.rect(0, 0, w, 40, "F");
    doc.setFillColor(57, 255, 20);
    doc.rect(0, 0, w, 3, "F");
    doc.setTextColor(57, 255, 20);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("CAR SPA", w / 2, 20, { align: "center" });
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(9);
    doc.text("Potvrzení převzetí vozidla", w / 2, 30, { align: "center" });

    let y = 55;
    doc.setTextColor(0, 0, 0);

    // Vehicle info box
    doc.setDrawColor(57, 255, 20);
    doc.setLineWidth(0.5);
    doc.rect(15, y - 5, w - 30, 28);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text("VOZIDLO", 20, y + 2);
    doc.text("SPZ", 20, y + 14);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(form.vehicle_brand, w - 20, y + 2, { align: "right" });
    doc.setTextColor(57, 180, 20);
    doc.text(form.vehicle_spz, w - 20, y + 14, { align: "right" });

    y += 40;

    // Program
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("PROGRAM OŠETŘENÍ", 20, y);
    y += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    const programLines = doc.splitTextToSize(form.programs, w - 40);
    doc.text(programLines, 20, y);
    y += programLines.length * 6 + 10;

    // Line
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.2);
    doc.line(20, y, w - 20, y);
    y += 12;

    // Price
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.text("CELKOVÁ CENA", 20, y);
    doc.setTextColor(57, 180, 20);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text(`${form.price.toLocaleString()} Kč`, w - 20, y + 2, { align: "right" });
    y += 20;

    // Contact
    doc.setDrawColor(220, 220, 220);
    doc.line(20, y, w - 20, y);
    y += 12;
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("KONTAKT", 20, y);
    y += 8;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Tel: ${form.customer_phone}`, 20, y);
    if (form.customer_email) {
      y += 6;
      doc.text(`E-mail: ${form.customer_email}`, 20, y);
    }

    y += 20;
    doc.setTextColor(130, 130, 130);
    doc.setFontSize(9);
    doc.text("O průběhu ošetření vás budeme informovat.", 20, y);
    y += 5;
    doc.text("Po dokončení vás kontaktujeme telefonicky.", 20, y);

    // Footer
    y = 270;
    doc.setDrawColor(57, 255, 20);
    doc.setLineWidth(0.3);
    doc.line(15, y, w - 15, y);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text("Car Spa | info@automyckacestlice.cz | www.carspa.cz", w / 2, y + 8, { align: "center" });

    doc.save(`CarSpa_${form.vehicle_spz.replace(/\s/g, "")}_${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success("PDF staženo!");
  };

  // Quick program buttons
  const quickPrograms = [
    ...packages.map(p => p.name),
    "Tekutý vosk", "Tuhý vosk", "Strojový vosk RUPES",
    "Ozonizace", "Čištění klimatizace"
  ];

  const addProgram = (name) => {
    const current = form.programs;
    if (current) {
      update("programs", current + " + " + name);
    } else {
      update("programs", name);
    }
  };

  if (done) {
    return (
      <div className="p-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-16 h-16 mx-auto mb-6 bg-[#39FF14] flex items-center justify-center"
        >
          <Check className="w-8 h-8 text-black" />
        </motion.div>
        <h3 className="text-xl font-bold mb-2">Vozidlo převzato</h3>
        <p className="text-zinc-400 text-sm mb-1">{form.vehicle_brand} — {form.vehicle_spz}</p>
        {form.customer_email ? (
          <p className="text-zinc-500 text-xs mb-6">E-mail s potvrzením odeslán na {form.customer_email}</p>
        ) : (
          <p className="text-zinc-500 text-xs mb-6">Stáhněte PDF a pošlete přes WhatsApp</p>
        )}
        <div className="flex flex-col gap-3">
          <button
            onClick={generatePDF}
            className="w-full flex items-center justify-center gap-2 bg-white/10 border border-[#39FF14]/30 text-[#39FF14] py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#39FF14]/10 transition-colors"
            data-testid="handover-download-pdf"
          >
            <Download className="w-4 h-4" /> Stáhnout PDF pro zákazníka
          </button>
          <button
            onClick={resetForm}
            className="w-full bg-[#39FF14] text-black py-3 text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors"
          >
            Další převzetí
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <Car className="w-5 h-5 text-[#39FF14]" />
        <h2 className="text-lg font-bold uppercase tracking-wider">Převzetí vozidla</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Contact */}
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
            <Phone className="w-3 h-3 inline mr-1" /> Telefon *
          </Label>
          <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)}
            className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-11"
            placeholder="+420 ..." data-testid="handover-phone" />
        </div>
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
            <Mail className="w-3 h-3 inline mr-1" /> E-mail
          </Label>
          <Input value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)}
            type="email" className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-11"
            placeholder="zakaznik@email.cz" data-testid="handover-email" />
        </div>

        {/* Vehicle */}
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
            <Car className="w-3 h-3 inline mr-1" /> Značka a model *
          </Label>
          <Input value={form.vehicle_brand} onChange={(e) => update("vehicle_brand", e.target.value)}
            className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-11"
            placeholder="BMW X5, Mercedes C220..." data-testid="handover-brand" />
        </div>
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
            <Hash className="w-3 h-3 inline mr-1" /> SPZ *
          </Label>
          <Input value={form.vehicle_spz} onChange={(e) => update("vehicle_spz", e.target.value.toUpperCase())}
            className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-11 uppercase font-bold tracking-wider"
            placeholder="1AB 2345" data-testid="handover-spz" />
        </div>

        {/* Name (optional) */}
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
            Jméno zákazníka
          </Label>
          <Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)}
            className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-11"
            placeholder="Nepovinné" />
        </div>

        {/* Location */}
        <div>
          <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
            Pobočka
          </Label>
          <select value={form.location} onChange={(e) => update("location", e.target.value)}
            className="w-full bg-white/5 border border-white/10 focus:border-[#39FF14] text-white rounded-none h-11 px-3 text-sm">
            <option value="cestlice" className="bg-[#0A0A0A]">Čestlice</option>
            <option value="butovice" className="bg-[#0A0A0A]">Butovice</option>
          </select>
        </div>
      </div>

      {/* Programs */}
      <div className="mt-5">
        <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-2 block">
          <FileText className="w-3 h-3 inline mr-1" /> Programy *
        </Label>
        <div className="flex flex-wrap gap-2 mb-3">
          {quickPrograms.map((p) => (
            <button key={p} onClick={() => addProgram(p)}
              className="px-3 py-1.5 text-xs bg-white/5 border border-white/10 text-zinc-400 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors">
              + {p}
            </button>
          ))}
        </div>
        <textarea value={form.programs} onChange={(e) => update("programs", e.target.value)}
          className="w-full bg-white/5 border border-white/10 focus:border-[#39FF14] focus:outline-none text-white rounded-none p-3 h-20 text-sm"
          placeholder="Např: Royal Leather Care + Strojový vosk RUPES + Ozonizace"
          data-testid="handover-programs" />
      </div>

      {/* Price */}
      <div className="mt-4">
        <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
          <Banknote className="w-3 h-3 inline mr-1" /> Cena (Kč) *
        </Label>
        <Input type="number" value={form.price || ""} onChange={(e) => update("price", parseInt(e.target.value) || 0)}
          className="bg-white/5 border-white/10 focus:border-[#39FF14] text-white rounded-none h-11 text-lg font-bold"
          placeholder="Zadejte cenu" data-testid="handover-price" />
      </div>

      {/* Notes */}
      <div className="mt-4">
        <Label className="text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 mb-1 block">
          <MessageSquare className="w-3 h-3 inline mr-1" /> Poznámka
        </Label>
        <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)}
          className="w-full bg-white/5 border border-white/10 focus:border-[#39FF14] focus:outline-none text-white rounded-none p-3 h-16 text-sm"
          placeholder="Speciální požadavky, stav vozu..." />
      </div>

      {/* Summary & Submit */}
      {form.vehicle_brand && form.price > 0 && (
        <div className="mt-6 bg-[#111] border border-[#39FF14]/20 p-4">
          <p className="text-xs text-[#39FF14] font-bold uppercase tracking-wider mb-2">Souhrn převzetí</p>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-500">Vozidlo</span>
            <span className="font-bold">{form.vehicle_brand} <span className="text-[#39FF14]">{form.vehicle_spz}</span></span>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-500">Program</span>
            <span className="text-xs text-zinc-300 max-w-[200px] text-right">{form.programs}</span>
          </div>
          <div className="flex justify-between text-sm border-t border-white/5 pt-2 mt-2">
            <span className="font-bold">Cena</span>
            <span className="text-[#39FF14] font-black text-xl">{form.price.toLocaleString()} Kč</span>
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={submitting || !form.customer_phone || !form.vehicle_brand || !form.vehicle_spz || !form.programs || !form.price}
        className="w-full mt-6 bg-[#39FF14] text-black py-4 text-sm font-bold uppercase tracking-wider hover:bg-white transition-colors disabled:bg-white/10 disabled:text-zinc-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="handover-submit"
      >
        <Send className="w-4 h-4" />
        {submitting ? "Odesílám..." : "Převzít vozidlo a odeslat potvrzení"}
      </button>
    </div>
  );
}
